// ============================================================
// MongoDBInstallation.jsx
//
// A branded, step-by-step guide for installing MongoDB Community
// Server and MongoDB Compass (GUI).
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #F9D71C  (Yellow / Warning & Attention)
//   Text        → #FFFFFF  (White)
//   Surface     → rgba(255,255,255,0.03)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    Button,
    Tab,
    Tabs,
    Avatar,
    Alert,
} from "@mui/material";

/* MUI Icons */
import DownloadIcon from "@mui/icons-material/Download";
import TerminalIcon from "@mui/icons-material/Terminal";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AppleIcon from "@mui/icons-material/Apple";
import WindowIcon from "@mui/icons-material/Window";
import ComputerIcon from "@mui/icons-material/Computer";
import SchoolIcon from "@mui/icons-material/School";
import ExploreIcon from "@mui/icons-material/Explore";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    green: "#00ED64",
    yellow: "#F9D71C",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function StepItem({ number, title, description }) {
    return (
        <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
            <Avatar sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900, width: 32, height: 32 }}>
                {number}
            </Avatar>
            <Box>
                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{description}</Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBInstallation() {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="Environment Setup · Module 03"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Environment <span style={{ color: BRAND.green }}>Setup</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Before writing BSON, we need the engine. Install the MongoDB Community Server and
                        Compass—the official GUI that makes exploring your data as easy as using a file explorer.
                    </Typography>
                </Box>

                {/* 2. OS SELECTOR TABS */}
                <Paper sx={{ bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 6 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            borderBottom: `1px solid ${BRAND.border}`,
                            "& .MuiTab-root": { color: BRAND.muted, fontWeight: 700 },
                            "& .Mui-selected": { color: BRAND.green },
                            "& .MuiTabs-indicator": { bgcolor: BRAND.green }
                        }}
                    >
                        <Tab icon={<WindowIcon />} label="Windows" />
                        <Tab icon={<AppleIcon />} label="macOS" />
                        <Tab icon={<ComputerIcon />} label="Linux (Ubuntu)" />
                    </Tabs>

                    <Box sx={{ p: { xs: 3, md: 6 } }}>
                        {tabValue === 0 && (
                            <Box>
                                <StepItem number="1" title="Download .msi Installer" description="Visit the MongoDB Download Center, select 'Community Server', and download the Windows 64-bit MSI." />
                                <StepItem number="2" title="Run Setup & Choose 'Complete'" description="Follow the wizard. Ensure 'Install MongoDB as a Service' is checked so it starts automatically." />
                                <StepItem number="3" title="Install MongoDB Compass" description="Look for the checkbox 'Install MongoDB Compass' at the end of the wizard to get the GUI automatically." />
                                <Alert severity="info" sx={{ bgcolor: "rgba(0,237,100,0.05)", color: BRAND.green, border: `1px solid ${BRAND.border}` }}>
                                    <strong>Pro Tip:</strong> Add <code>C:\Program Files\MongoDB\Server\X.X\bin</code> to your System Environment PATH to use <code>mongosh</code> from any terminal.
                                </Alert>
                            </Box>
                        )}

                        {tabValue === 1 && (
                            <Box>
                                <StepItem number="1" title="Install Homebrew" description="If you don't have it, run: /bin/bash -c '$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)'" />
                                <StepItem number="2" title="Tap MongoDB" description="Run: brew tap mongodb/brew" />
                                <StepItem number="3" title="Install Server" description="Run: brew install mongodb-community@7.0" />
                                <StepItem number="4" title="Start Service" description="Run: brew services start mongodb-community@7.0" />
                            </Box>
                        )}

                        {tabValue === 2 && (
                            <Box>
                                <StepItem number="1" title="Import Public Key" description="Import the GPG key for the official MongoDB repository so Ubuntu trusts the package." />
                                <StepItem number="2" title="Create List File" description="Add the MongoDB repository details to your sources.list.d directory." />
                                <StepItem number="3" title="Update & Install" description="Run: sudo apt-get update && sudo apt-get install -y mongodb-org" />
                                <StepItem number="4" title="Enable systemd" description="Run: sudo systemctl enable mongod && sudo systemctl start mongod" />
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* 3. GUI EXPLORATION (COMPASS) */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <ExploreIcon size={32} color={BRAND.green} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Visualize with Compass</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            You don't always need the terminal. <strong>MongoDB Compass</strong> is the GUI that
                            allows you to analyze your documents, run visual aggregations, and optimize indexes
                            without typing a single line of code.
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <CheckCircleIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                                <Typography variant="body2">Visual Schema Analysis</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <CheckCircleIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                                <Typography variant="body2">Real-time Performance Graphs</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <CheckCircleIcon sx={{ color: BRAND.green, fontSize: 20 }} />
                                <Typography variant="body2">Built-in MongoDB Shell</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 1, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, overflow: "hidden" }}>

                        </Paper>
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 4. VERIFYING THE CONNECTION */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <TerminalIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The First Connection Test</Typography>
                    </Box>

                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        Once installed, open your Terminal or PowerShell and type the following command to
                        enter the <strong>MongoDB Shell</strong>.
                    </Typography>

                    <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, p: 3, mb: 4 }}>
                        <Typography sx={{ fontFamily: "'Fira Code', monospace", color: BRAND.green }}>
                            $ mongosh
                        </Typography>
                        <Typography sx={{ fontFamily: "'Fira Code', monospace", color: BRAND.muted, mt: 1 }}>
                            Connecting to: mongodb://127.0.0.1:27017/ ... <br/>
                            Welcome to the MongoDB shell (mongosh)!
                        </Typography>
                    </Box>



                    <Alert severity="warning" sx={{ bgcolor: "rgba(249,215,28,0.05)", color: BRAND.yellow, border: `1px solid ${BRAND.yellow}` }}>
                        <strong>Firewall Note:</strong> By default, MongoDB listens on port <strong>27017</strong>. Ensure your local firewall isn't blocking this port if you encounter connection timeouts.
                    </Alert>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <SettingsIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>System Ready?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Your engine is running. It's time to enter the shell and run your first commands.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Enter the Shell</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Troubleshoot Install</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
